import type { SalesDemoScenario, SalesDemoVertical } from "./types";

const gastroScenarios: readonly SalesDemoScenario[] = Object.freeze([
  {
    id: "recommendation",
    name: "Consulta y recomendación",
    summary: "Entiende preferencias y recomienda usando la carta disponible.",
    steps: Object.freeze([
      {
        id: "recommendation-1",
        customerMessage: "Hola, somos 4 y queremos pedir algo para compartir. ¿Qué nos recomendás?",
        assistantMessage: "Claro. ¿Hay alguna preferencia o restricción que tenga que tener en cuenta? Por ejemplo vegetariano, sin gluten o alguna alergia.",
        actions: Object.freeze([
          { capability: "conversation.intent.detect", label: "Entendió qué necesita el cliente", detail: "Detectó una consulta de recomendación y pidió sólo el dato que cambia la respuesta." },
        ]),
      },
      {
        id: "recommendation-2",
        customerMessage: "Sí, una persona es vegetariana.",
        assistantMessage: "Perfecto. Para 4 les recomendaría una entrada para compartir y una pizza grande mitad clásica y mitad vegetariana. También puedo mostrarte sólo las opciones vegetarianas disponibles hoy.",
        actions: Object.freeze([
          { capability: "catalog.search", label: "Consultó la carta disponible", detail: "Filtró opciones que pueden compartirse y que contemplan una alternativa vegetariana." },
          { capability: "customer.preference.capture", label: "Recordó la preferencia del grupo", detail: "Dejó registrada la necesidad vegetariana para mantener contexto durante la conversación." },
        ]),
      },
    ]),
  },
  {
    id: "reservation",
    name: "Reserva de mesa",
    summary: "Pregunta lo mínimo, consulta disponibilidad y registra la reserva.",
    steps: Object.freeze([
      {
        id: "reservation-1",
        customerMessage: "¿Tienen mesa para esta noche?",
        assistantMessage: "Sí, puedo revisarlo. ¿Para cuántas personas y aproximadamente a qué hora?",
        actions: Object.freeze([
          { capability: "conversation.intent.detect", label: "Detectó intención de reserva", detail: "Identificó que faltan cantidad de personas y horario antes de consultar disponibilidad." },
        ]),
      },
      {
        id: "reservation-2",
        customerMessage: "Somos 6, a las 21.",
        assistantMessage: "Hay disponibilidad para 6 personas a las 21:00. ¿A nombre de quién dejo la reserva?",
        actions: Object.freeze([
          { capability: "availability.check", label: "Consultó disponibilidad real", detail: "Verificó capacidad para 6 personas a las 21:00 antes de prometer la mesa." },
        ]),
      },
      {
        id: "reservation-3",
        customerMessage: "Alexi. Mi teléfono es 351 555 0198.",
        assistantMessage: "Listo, Alexi. Quedó reservada una mesa para 6 hoy a las 21:00. Si cambia algo, podés responder por acá.",
        actions: Object.freeze([
          { capability: "reservation.create", label: "Creó la reserva", detail: "Registró horario, cantidad de personas y datos del cliente." },
          { capability: "crm.upsert", label: "Guardó al cliente en CRM", detail: "El contacto queda identificado para futuras conversaciones y seguimiento." },
        ]),
      },
    ]),
  },
  {
    id: "order",
    name: "Tomar un pedido",
    summary: "Valida productos, modalidad y total antes de confirmar.",
    steps: Object.freeze([
      {
        id: "order-1",
        customerMessage: "Quiero pedir dos pizzas grandes para esta noche.",
        assistantMessage: "Perfecto. ¿Cuáles querés y es para retirar, delivery o comer en el local?",
        actions: Object.freeze([
          { capability: "conversation.intent.detect", label: "Entendió que quiere comprar", detail: "Abrió un pedido y pidió solamente productos y modalidad de entrega." },
        ]),
      },
      {
        id: "order-2",
        customerMessage: "Una muzzarella y una napolitana, para retirar a las 21:30.",
        assistantMessage: "Las dos están disponibles. Te preparo 1 muzzarella grande + 1 napolitana grande para retirar a las 21:30. El total sería $31.500. ¿Confirmo?",
        actions: Object.freeze([
          { capability: "catalog.search", label: "Validó productos y disponibilidad", detail: "Comprobó que las dos pizzas puedan venderse antes de confirmar." },
          { capability: "order.quote", label: "Calculó el total", detail: "Preparó líneas, modalidad, horario y precio del pedido." },
        ]),
      },
      {
        id: "order-3",
        customerMessage: "Sí, confirmalo.",
        assistantMessage: "Confirmado. Tu pedido queda preparado para retirar a las 21:30. Te aviso por acá si hay alguna novedad.",
        actions: Object.freeze([
          { capability: "order.create", label: "Creó el pedido", detail: "La orden quedó registrada y lista para entrar en la operación del negocio." },
          { capability: "crm.upsert", label: "Asoció el pedido al cliente", detail: "La compra queda vinculada al historial del contacto." },
        ]),
      },
    ]),
  },
  {
    id: "allergy",
    name: "Alergias y dudas específicas",
    summary: "Consulta conocimiento del negocio y evita inventar respuestas sensibles.",
    steps: Object.freeze([
      {
        id: "allergy-1",
        customerMessage: "Uno de nosotros es celíaco. ¿Qué puede comer?",
        assistantMessage: "Puedo ayudarte con lo que el negocio tiene documentado. Hay opciones marcadas como sin gluten, pero antes de recomendarte necesito aclarar si necesitás evitar también contaminación cruzada.",
        actions: Object.freeze([
          { capability: "knowledge.search", label: "Consultó información del negocio", detail: "Buscó la información documentada sobre opciones sin gluten y protocolos de cocina." },
          { capability: "safety.constraint.apply", label: "Evitó afirmar algo no confirmado", detail: "Pidió una aclaración importante en vez de asumir que una opción es segura para cualquier caso." },
        ]),
      },
      {
        id: "allergy-2",
        customerMessage: "Sí, necesitamos evitar contaminación cruzada.",
        assistantMessage: "En ese caso prefiero no asegurarte una opción sin confirmación del equipo. Te derivo con una persona del local para que puedan indicarte qué pueden preparar hoy con seguridad.",
        actions: Object.freeze([
          { capability: "conversation.handoff", label: "Derivó la conversación a una persona", detail: "Conservó el contexto y escaló el caso cuando hacía falta una confirmación humana." },
        ]),
      },
    ]),
  },
  {
    id: "handoff",
    name: "Derivación a una persona",
    summary: "Reconoce cuándo no conviene automatizar y entrega el contexto al equipo.",
    steps: Object.freeze([
      {
        id: "handoff-1",
        customerMessage: "Tuve un problema con mi pedido de ayer y quiero hablar con alguien.",
        assistantMessage: "Claro. Para que no tengas que repetir todo, contame brevemente qué pasó y dejo la conversación preparada para el equipo.",
        actions: Object.freeze([
          { capability: "support.case.prepare", label: "Preparó el caso", detail: "Identificó que se trata de un reclamo y empezó a reunir el contexto útil." },
        ]),
      },
      {
        id: "handoff-2",
        customerMessage: "Llegó una pizza equivocada y ya había pagado.",
        assistantMessage: "Entendido. Dejé registrado que el pedido de ayer llegó incorrecto y que ya estaba pagado. Te paso con una persona del local con este contexto para que lo resuelva.",
        actions: Object.freeze([
          { capability: "crm.timeline.append", label: "Guardó el contexto del problema", detail: "El equipo puede ver qué ocurrió sin pedirle al cliente que vuelva a explicarlo." },
          { capability: "conversation.handoff", label: "Asignó la conversación al equipo", detail: "Derivó el caso a atención humana conservando todo el contexto." },
        ]),
      },
    ]),
  },
]);

const servicesScenarios: readonly SalesDemoScenario[] = Object.freeze([
  {
    id: "lead",
    name: "Consulta y calificación",
    summary: "Entiende la necesidad y recopila sólo la información útil para vender.",
    steps: Object.freeze([
      {
        id: "lead-1",
        customerMessage: "Necesito reparar una filtración en el techo. ¿Trabajan en Villa Carlos Paz?",
        assistantMessage: "Sí, trabajamos en Villa Carlos Paz. ¿La filtración aparece sólo cuando llueve o también en otros momentos?",
        actions: Object.freeze([
          { capability: "lead.capture", label: "Creó una oportunidad comercial", detail: "Registró servicio solicitado y localidad del potencial cliente." },
        ]),
      },
      {
        id: "lead-2",
        customerMessage: "Sólo cuando llueve, sobre todo en una esquina del living.",
        assistantMessage: "Perfecto. Con eso ya puedo dejar el caso preparado. Si me pasás una foto y una dirección aproximada, podemos coordinar una visita técnica.",
        actions: Object.freeze([
          { capability: "lead.qualify", label: "Calificó la consulta", detail: "Registró síntomas útiles para que el técnico llegue con contexto." },
          { capability: "crm.upsert", label: "Actualizó CRM", detail: "El contacto queda listo para seguimiento comercial." },
        ]),
      },
    ]),
  },
  {
    id: "appointment",
    name: "Agendar visita",
    summary: "Consulta agenda y confirma una visita sin intercambio manual innecesario.",
    steps: Object.freeze([
      {
        id: "appointment-1",
        customerMessage: "¿Pueden venir el martes por la tarde?",
        assistantMessage: "Sí. El martes tengo disponibilidad a las 16:00 y a las 17:30. ¿Cuál te queda mejor?",
        actions: Object.freeze([
          { capability: "availability.check", label: "Consultó la agenda", detail: "Buscó horarios realmente disponibles dentro de la franja solicitada." },
        ]),
      },
      {
        id: "appointment-2",
        customerMessage: "A las 17:30 está perfecto.",
        assistantMessage: "Listo. Dejé agendada la visita para el martes a las 17:30. Te mando la confirmación por acá.",
        actions: Object.freeze([
          { capability: "appointment.create", label: "Agendó la visita", detail: "Reservó el horario y lo vinculó al cliente y a la oportunidad." },
          { capability: "crm.timeline.append", label: "Actualizó el historial", detail: "La próxima acción queda visible para el equipo." },
        ]),
      },
    ]),
  },
  {
    id: "follow-up",
    name: "Seguimiento comercial",
    summary: "Mantiene contexto y deja preparada la siguiente acción de venta.",
    steps: Object.freeze([
      {
        id: "follow-up-1",
        customerMessage: "Mandame el presupuesto por WhatsApp y lo veo con mi pareja.",
        assistantMessage: "Claro. Te lo dejo asociado a esta conversación y marcado como presupuesto enviado. ¿Querés que te escribamos de nuevo en unos días si todavía no lo decidieron?",
        actions: Object.freeze([
          { capability: "quote.send", label: "Preparó el presupuesto", detail: "Asoció el presupuesto al contacto y a la oportunidad." },
          { capability: "crm.stage.update", label: "Actualizó la etapa comercial", detail: "El lead quedó marcado como presupuesto enviado." },
        ]),
      },
      {
        id: "follow-up-2",
        customerMessage: "Sí, escribime el lunes.",
        assistantMessage: "Perfecto. Dejé programado un seguimiento para el lunes y vamos a conservar todo este contexto.",
        actions: Object.freeze([
          { capability: "automation.schedule", label: "Programó el seguimiento", detail: "Dejó creada la próxima acción comercial sin que el equipo tenga que acordarse manualmente." },
        ]),
      },
    ]),
  },
]);

export const SALES_DEMO_VERTICALS: readonly SalesDemoVertical[] = Object.freeze([
  {
    id: "gastronomia",
    name: "Gastronomía",
    defaultBusinessName: "Casa Nativa",
    description: "Atención, reservas, pedidos, conocimiento del negocio y derivación humana.",
    capabilities: Object.freeze(["Carta / catálogo", "Disponibilidad", "Reservas", "Pedidos", "CRM", "Conocimiento", "Derivación humana"]),
    scenarios: gastroScenarios,
  },
  {
    id: "servicios-locales",
    name: "Servicios locales",
    defaultBusinessName: "Demo Servicios",
    description: "Captación, calificación, agenda, presupuestos, CRM y seguimiento comercial.",
    capabilities: Object.freeze(["Captura de leads", "Calificación", "Agenda / visitas", "Presupuestos", "CRM", "Seguimiento automático"]),
    scenarios: servicesScenarios,
  },
]);

export function salesDemoVertical(id: string | null): SalesDemoVertical {
  return SALES_DEMO_VERTICALS.find((vertical) => vertical.id === id) ?? SALES_DEMO_VERTICALS[0]!;
}

export function salesDemoScenarioForText(vertical: SalesDemoVertical, text: string): SalesDemoScenario {
  const normalized = text.toLocaleLowerCase("es");
  if (vertical.id === "gastronomia") {
    if (/reserv|mesa|personas|lugar|disponib/.test(normalized)) return vertical.scenarios.find((scenario) => scenario.id === "reservation")!;
    if (/pedido|pizza|retir|delivery|orden|comprar/.test(normalized)) return vertical.scenarios.find((scenario) => scenario.id === "order")!;
    if (/celiac|gluten|alerg|intoler|contamin/.test(normalized)) return vertical.scenarios.find((scenario) => scenario.id === "allergy")!;
    if (/problema|reclamo|queja|persona|alguien|humano/.test(normalized)) return vertical.scenarios.find((scenario) => scenario.id === "handoff")!;
    return vertical.scenarios.find((scenario) => scenario.id === "recommendation")!;
  }
  if (/martes|miércoles|miercoles|jueves|viernes|turno|agend|visita|hora/.test(normalized)) return vertical.scenarios.find((scenario) => scenario.id === "appointment")!;
  if (/presupuesto|whatsapp|seguimiento|lunes|decidir/.test(normalized)) return vertical.scenarios.find((scenario) => scenario.id === "follow-up")!;
  return vertical.scenarios.find((scenario) => scenario.id === "lead")!;
}
